import type { SegmentationDialogEntry, SegmentationSettings } from '@/src/components/segmentation/segmentation-typing';

import { computed, nextTick, ref, watch } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { SEGMENTATION_SETTINGS_SCHEMA } from '@/src/components/segmentation/segmentation-typing';
import { useAlert } from '@/src/composables/useAlert';
import { useBackendSegmentation } from '@/src/composables/useBackendSegmentation';
import { useSegmentationSettingsStore } from '@/src/store/contours/contour-segmentation';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { Orientation } from '@/src/store/dicom-web/classification-typing';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { seriesClassification } from '@/src/store/dicom-web/SeriesClassification';

import { getMessage } from '../utils/exception';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ViewerSegmentationSummary {
  viewerID: string;
  orientation: Orientation;
  imageCount: number;
  seriesIdx: number;
  sliceIdx: number;
}

// ── Singleton state (shared across all callers) ────────────────────────────────

const showDialog = ref(false);
const viewerSummaries = ref<ViewerSegmentationSummary[]>([]);
const isStartingSegmentation = ref(false);

// Per-viewer selections
const selectedOrientations = ref<Record<string, Orientation>>({});
const selectedImageScope = ref<Record<string, string>>({});

// Tracks which viewers had imageUIDs pre-provided by the caller
const preProvidedViewerUIDs = ref<Record<string, string[]>>({});

// Tracks whether duplicate viewerIDs were passed into trigger()
const duplicateViewerIDDetected = ref(false);

// Model selection
const modelSettingOptions = SEGMENTATION_SETTINGS_SCHEMA.SAX_MODEL_NAME?.optionsRaw;

const selectedModel = ref<SegmentationSettings['SAX_MODEL_NAME']>('auto');

// Available orientations for the dropdown
const availableOrientations = [
  Orientation.Undefined,
  Orientation.Sax,
  Orientation.Lax2CV,
  Orientation.Lax3CV,
  Orientation.Lax4CV,
];

// ── Computed (module-level, derived from singleton refs) ────────────────────

const hasSummaries = computed(() => viewerSummaries.value.length > 0);

const uniqueViewerSummaries = computed(() => {
  const seen = new Set<string>();
  return viewerSummaries.value.filter((summary) => {
    const key = `${summary.seriesIdx}-${summary.sliceIdx}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const hasUndefinedOrientation = computed(() =>
  uniqueViewerSummaries.value.some(
    summary => summary.orientation === Orientation.Undefined,
  ),
);

const hasDuplicateSeriesSlice = computed(
  () => uniqueViewerSummaries.value.length < viewerSummaries.value.length,
);

const hasDuplicateViewerID = computed(() => duplicateViewerIDDetected.value);

// ── Composable ─────────────────────────────────────────────────────────────────

export function useSegmentationDialog() {
  // ── Dependencies (resolved at call-time, inside setup context) ───────────
  const backendSegmentation = useBackendSegmentation();
  const currentStudyStore = useCurrentStudyStore();
  const viewportActions = useViewportActions();
  const alert = useAlert();
  const segmentationSettingsStore = useSegmentationSettingsStore();
  const viewportStateStore = useViewportStateStore();

  const isSegmentationActive = computed(
    () => backendSegmentation.isActive.value,
  );
  const segmentationRunningAlertId = ref<string | null>(null);

  // Initialize model from store on first composable use
  const storedModel = segmentationSettingsStore.getSettings().SAX_MODEL_NAME;
  if (storedModel) {
    selectedModel.value = storedModel;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Returns true if the caller pre-supplied imageUIDs for this viewer,
   * meaning the scope selector should be hidden.
   */
  function hasPreProvidedUIDs(viewerID: string): boolean {
    return viewerID in preProvidedViewerUIDs.value;
  }

  /**
   * Get the effective image UIDs for a viewer based on scope and pre-provided UIDs.
   */
  function getEffectiveImageUIDs(viewerID: string): string[] {
    // If the caller pre-supplied UIDs, use those directly
    if (hasPreProvidedUIDs(viewerID)) {
      return preProvidedViewerUIDs.value[viewerID];
    }

    // Otherwise, collect from the viewer based on selected scope
    const imageUIDsAll
      = currentStudyStore.getSliceImageUIDsbyView(viewerID) ?? [];
    if (imageUIDsAll.length === 0) return [];

    const studyObj = viewportActions.getStudyObjectByViewerId(viewerID);
    const excludedImages = studyObj?.getExcludedImages() ?? new Set<string>();
    const scope = selectedImageScope.value[viewerID] || 'Breathhold';

    if (scope === 'All in Slice') {
      return imageUIDsAll.filter((u: string) => !excludedImages.has(u));
    }

    // Breathhold (default)
    const breathhold = studyObj?.getBreathhold();
    const start = breathhold?.getStart();
    const end = breathhold?.getEnd();
    const cropped = imageUIDsAll.slice(start, end && end + 1);
    return cropped.filter((u: string) => !excludedImages.has(u));
  }

  /**
   * Get breathhold range text for a viewer (for template display).
   */
  function getBreathholdRange(viewerID: string): string {
    try {
      const studyObj = viewportActions.getStudyObjectByViewerId(viewerID);
      if (!studyObj) return '';
      const bh = studyObj.getBreathhold();
      return `${bh.getStart() + 1} - ${bh.getEnd() + 1}`;
    }
    catch {
      return '';
    }
  }

  // ── Core methods ────────────────────────────────────────────────────────────

  /**
   * Build viewer summaries from the list of deduplicated viewerIDs.
   */
  function buildViewerSummaries(viewerIDs: string[]) {
    viewerSummaries.value = viewerIDs.map((viewerID) => {
      const { seriesIdx, sliceIdx }
        = viewportStateStore.getSelectedImageIndices(viewerID);

      const includedUIDs = getEffectiveImageUIDs(viewerID);

      // If there are no images and no pre-provided UIDs,
      // this viewer is likely not properly initialized.
      if (includedUIDs.length === 0 && !hasPreProvidedUIDs(viewerID)) {
        console.warn(
          `No images found for viewer ${viewerID}. Ensure viewer is displaying image data before segmentation.`,
        );
      }

      const orientation
        = seriesClassification.getClassificationByViewport(viewerID)
          ?.orientation ?? Orientation.Undefined;

      // Initialize per-viewer selections (only if not already set)
      if (!(viewerID in selectedOrientations.value)) {
        selectedOrientations.value[viewerID] = orientation;
      }
      if (!(viewerID in selectedImageScope.value)) {
        selectedImageScope.value[viewerID] = 'Breathhold';
      }

      return {
        viewerID,
        orientation,
        imageCount: includedUIDs.length,
        seriesIdx,
        sliceIdx,
      };
    });
  }

  //  /**
  //  * Abort active segmentation and close the dialog.
  //  */
  function abort() {
    backendSegmentation.abort();
    showDialog.value = false;
  }

  /**
   * Main entry point — open the dialog for a set of viewer entries.
   *
   * @example
   * const segDialog = useSegmentationDialog();
   * segDialog.trigger([
   *   { viewerID: 'ContoursLeft', imageUIDs: ['uid1', 'uid2'] },
   *   { viewerID: 'ContoursRight' },
   * ]);
   */
  function trigger(entries: SegmentationDialogEntry[]) {
    // Reset state
    selectedOrientations.value = {};
    selectedImageScope.value = {};
    preProvidedViewerUIDs.value = {};
    duplicateViewerIDDetected.value = false;

    // Deduplicate by viewerID — keep only the first occurrence
    const seen = new Set<string>();
    const dedupedEntries: SegmentationDialogEntry[] = [];

    for (const entry of entries) {
      if (seen.has(entry.viewerID)) {
        duplicateViewerIDDetected.value = true;
        continue;
      }
      seen.add(entry.viewerID);
      dedupedEntries.push(entry);
    }

    // Record which viewers have pre-provided imageUIDs

    for (const entry of dedupedEntries) {
      if (entry.imageUIDs && entry.imageUIDs.length > 0) {
        preProvidedViewerUIDs.value[entry.viewerID] = entry.imageUIDs;
      }
    }

    const viewerIDs = dedupedEntries.map(e => e.viewerID);
    buildViewerSummaries(viewerIDs);
    showDialog.value = true;
  }

  /**
   * Validate everything and start the segmentation jobs.
   */
  async function startSegmentation() {
    // Validate orientations
    const hasUndefined = uniqueViewerSummaries.value.some((summary) => {
      const o
        = selectedOrientations.value[summary.viewerID] ?? Orientation.Undefined;
      return o === Orientation.Undefined;
    });
    if (hasUndefined) {
      alert.error(
        'Please define an orientation classification for every selected viewer before segmentation.',
      );
      return;
    }

    // Update global model selection
    segmentationSettingsStore.updateSettings({
      SAX_MODEL_NAME: selectedModel.value,
    });

    isStartingSegmentation.value = true;
    try {
      // Build jobs only for unique viewers (no duplicate series/slice)
      const jobs = uniqueViewerSummaries.value.map((summary) => {
        const vid = summary.viewerID;
        const includeUIDs = getEffectiveImageUIDs(vid);

        return {
          viewerID: vid,
          imageUIDs: includeUIDs,
          orientation: selectedOrientations.value[vid],
        };
      });

      void backendSegmentation.segmentJobs(jobs, false, true);
      showDialog.value = false;
      segmentationRunningAlertId.value = alert.info(
        `Segmentation running please wait...`,
        true,
      );
    }
    catch (error: any) {
      alert.error(
        `Segmentation failed: ${getMessage(error)}`,
      );
    }
    finally {
      isStartingSegmentation.value = false;
    }
  }

  /**
   * Update orientation for a viewer, persist the classification, and rebuild summaries.
   */
  function handleOrientationUpdate(
    viewerID: string,
    newOrientation: Orientation,
  ) {
    selectedOrientations.value[viewerID] = newOrientation;
    seriesClassification.adjustClassificationByViewport(viewerID, {
      orientation: newOrientation,
      mode: 'Manual',
    });
    void nextTick(() => {
      const viewerIDs = viewerSummaries.value.map(s => s.viewerID);
      buildViewerSummaries(viewerIDs);
    });
  }

  // ── Watchers ────────────────────────────────────────────────────────────────

  // When the user changes the image scope for any viewer, rebuild summaries
  watch(
    selectedImageScope,
    () => {
      if (viewerSummaries.value.length > 0) {
        const viewerIDs = viewerSummaries.value.map(s => s.viewerID);
        buildViewerSummaries(viewerIDs);
      }
    },
    { deep: true },
  );

  watch(isSegmentationActive, (active) => {
    if (!active && segmentationRunningAlertId.value) {
      alert.hide(segmentationRunningAlertId.value);
      segmentationRunningAlertId.value = null;
    }
  });

  // ── Public API ──────────────────────────────────────────────────────────────

  return {
    // Singleton reactive state
    showDialog,
    viewerSummaries,
    uniqueViewerSummaries,
    selectedOrientations,
    selectedImageScope,
    selectedModel,
    isStartingSegmentation,
    isSegmentationActive,
    hasSummaries,
    hasUndefinedOrientation,
    hasDuplicateSeriesSlice,
    hasDuplicateViewerID,
    modelSettingOptions,
    availableOrientations,

    // Methods
    trigger,
    startSegmentation,
    handleOrientationUpdate,
    abort,
    hasPreProvidedUIDs,
    getBreathholdRange,
  };
}
